import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone, Check, X as XIcon } from "lucide-react";

const UPSELL_OPTIONS = [
  { key: "delivery", label: "Free Delivery ($39 value)" },
  { key: "delivery_install", label: "Free Delivery + Install ($99 value)" },
  { key: "pro_measurement", label: "Free Pro Measurement ($39 value)" },
  { key: "white_glove", label: "Free White Glove Install" },
];

const blankForm = {
  name: "",
  type: "free_upsell",
  threshold_amount: 300,
  active: true,
  banner_text: "",
  free_upsell_keys: ["delivery"],
  percent: 10,
};

export default function AdminPromotions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/promotions");
      setRows(r.data || []);
    } catch {
      toast.error("Couldn't load promotions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    if (form.type === "free_upsell" && form.free_upsell_keys.length === 0) {
      return toast.error("Pick at least one upsell to make free");
    }
    if (form.type === "percent_off" && !(form.percent > 0)) {
      return toast.error("Percent must be > 0");
    }
    setSaving(true);
    try {
      await api.post("/admin/promotions", {
        ...form,
        threshold_amount: Number(form.threshold_amount) || 0,
        percent: Number(form.percent) || 0,
      });
      toast.success("Promotion created");
      setForm(blankForm);
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row) => {
    try {
      await api.patch(`/admin/promotions/${row.id}`, { active: !row.active });
      toast.success(`${row.name} ${!row.active ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/promotions/${row.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleKey = (k) => {
    const cur = new Set(form.free_upsell_keys);
    cur.has(k) ? cur.delete(k) : cur.add(k);
    setForm({ ...form, free_upsell_keys: [...cur] });
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-text)]">Promotions</h1>
          <p className="text-sm muted-text mt-1">
            Threshold-based promos that show across the site. <strong>Only one promo can be active at a time</strong> — enabling a new one auto-disables the others.
          </p>
        </div>
        <button
          data-testid="new-promo-btn"
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm brand-bg text-white"
        >
          <Plus className="w-4 h-4" /> New promo
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={create}
          data-testid="new-promo-form"
          className="bg-white rounded-2xl border-2 border-[var(--brand-green)] p-4 mb-5 space-y-3"
        >
          <div>
            <label className="text-xs font-semibold muted-text">Internal name</label>
            <input
              data-testid="form-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Spring 2026 — Free Delivery over $300"
              maxLength={80}
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold muted-text">Promo type</label>
              <div className="flex gap-2 mt-1">
                {[
                  { v: "free_upsell", l: "Unlock free upsell(s)" },
                  { v: "percent_off", l: "Extra % off products" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`form-type-${opt.v}`}
                    onClick={() => setForm({ ...form, type: opt.v })}
                    className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold border-2 transition ${
                      form.type === opt.v
                        ? "brand-bg text-white border-transparent"
                        : "border-[var(--brand-border)]"
                    }`}
                  >{opt.l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold muted-text">Threshold ($, pre-tax)</label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-2 text-sm muted-text">$</span>
                <input
                  data-testid="form-threshold"
                  type="number" min="0" step="1"
                  value={form.threshold_amount}
                  onChange={(e) => setForm({ ...form, threshold_amount: e.target.value })}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] pl-7 pr-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {form.type === "free_upsell" ? (
            <div>
              <label className="text-xs font-semibold muted-text">Which upsells become free?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {UPSELL_OPTIONS.map((u) => (
                  <label key={u.key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer transition ${
                    form.free_upsell_keys.includes(u.key)
                      ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]"
                      : "border-[var(--brand-border)]"
                  }`}>
                    <input
                      type="checkbox"
                      data-testid={`form-key-${u.key}`}
                      checked={form.free_upsell_keys.includes(u.key)}
                      onChange={() => toggleKey(u.key)}
                    />
                    <span className="text-sm">{u.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold muted-text">Extra percent off products</label>
              <div className="relative mt-1 w-32">
                <input
                  data-testid="form-percent"
                  type="number" min="1" max="100" step="0.5"
                  value={form.percent}
                  onChange={(e) => setForm({ ...form, percent: e.target.value })}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm pr-7"
                />
                <span className="absolute right-3 top-2 text-sm muted-text">%</span>
              </div>
              <p className="text-[11px] muted-text mt-1">Stacks with discount codes.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold muted-text">Banner text (shown to customers)</label>
            <input
              data-testid="form-banner-text"
              value={form.banner_text}
              onChange={(e) => setForm({ ...form, banner_text: e.target.value })}
              placeholder={form.type === "free_upsell" ? "Free Delivery on orders over $300!" : "Save an extra 10% on orders over $300!"}
              maxLength={140}
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1"
            />
          </div>

          <label className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[var(--brand-border)] cursor-pointer w-fit">
            <input
              type="checkbox"
              data-testid="form-active"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span className="text-sm">Activate now (auto-disables any other active promo)</span>
          </label>

          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-[var(--brand-border)]">Cancel</button>
            <button
              data-testid="form-submit"
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-full text-sm font-semibold brand-bg text-white disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create promo"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--brand-border)] p-8 text-center muted-text" data-testid="promos-empty">
          <Megaphone className="w-6 h-6 mx-auto mb-2 muted-text" />
          <p>No promotions yet. Click <strong>New promo</strong> to create one.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="promo-list">
          {rows.map((row) => (
            <div
              key={row.id}
              data-testid={`promo-row-${row.id}`}
              className={`bg-white rounded-2xl border p-4 flex items-center gap-3 ${
                row.active ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]" : "border-[var(--brand-border)] opacity-70"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-[var(--brand-text)]">{row.name}</span>
                  {row.active ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "#dcfce7", color: "#166534" }}>active</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ background: "#e5e7eb", color: "#374151" }}>disabled</span>
                  )}
                  <span className="text-[11px] muted-text">
                    {row.type === "free_upsell"
                      ? `Free: ${(row.free_upsell_keys || []).join(", ")}`
                      : `+${row.percent}% off products`}
                    {" "}· over ${row.threshold_amount}
                  </span>
                </div>
                {row.banner_text && (
                  <p className="text-xs muted-text mt-0.5 italic">&ldquo;{row.banner_text}&rdquo;</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  data-testid={`toggle-${row.id}`}
                  onClick={() => toggle(row)}
                  title={row.active ? "Disable" : "Enable"}
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    row.active
                      ? "border-[var(--brand-border)] hover:border-red-300 text-[var(--brand-text)]"
                      : "border-[var(--brand-border)] hover:border-[var(--brand-green)] brand-text"
                  }`}
                >
                  {row.active ? <XIcon className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`delete-${row.id}`}
                  onClick={() => remove(row)}
                  className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-red-200 text-red-600 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
